import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Locale, Translations } from './types';
import { es } from './es';
import { en } from './en';
import { getLocaleFromPath, getAlternateUrl, getRoutes } from './routes';
import { trackLanguageSwitch } from '@/seo/analytics';

const translations: Record<Locale, Translations> = { es, en };

interface I18nContextType {
  locale: Locale;
  t: Translations;
  setLocale: (locale: Locale) => void;
  routes: ReturnType<typeof getRoutes>;
  getAlternateUrl: (targetLocale: Locale) => string;
}

const I18nContext = createContext<I18nContextType>({
  locale: 'es',
  t: es,
  setLocale: () => {},
  routes: getRoutes('es'),
  getAlternateUrl: () => '/es',
});

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [locale, setLocaleState] = useState<Locale>(() => {
    const pathLocale = getLocaleFromPath(location.pathname);
    if (location.pathname.startsWith('/es') || location.pathname.startsWith('/en')) {
      return pathLocale;
    }
    const saved = localStorage.getItem('locale') as Locale;
    if (saved === 'en' || saved === 'es') return saved;
    return 'es';
  });

  const currentRoutes = getRoutes(locale);

  const setLocale = useCallback((l: Locale) => {
    const prev = locale;
    setLocaleState(l);
    localStorage.setItem('locale', l);
    document.documentElement.lang = l;
    trackLanguageSwitch(prev, l);
    const newPath = getAlternateUrl(location.pathname, l);
    navigate(newPath, { replace: true });
  }, [location.pathname, navigate, locale]);

  useEffect(() => {
    if (location.pathname.startsWith('/es') || location.pathname.startsWith('/en')) {
      const pathLocale = getLocaleFromPath(location.pathname);
      if (pathLocale !== locale) {
        setLocaleState(pathLocale);
        localStorage.setItem('locale', pathLocale);
      }
    }
  }, [location.pathname, locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const getAlt = useCallback((targetLocale: Locale) => {
    return getAlternateUrl(location.pathname, targetLocale);
  }, [location.pathname]);

  return (
    <I18nContext.Provider value={{
      locale,
      t: translations[locale],
      setLocale,
      routes: currentRoutes,
      getAlternateUrl: getAlt,
    }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
